/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Tiny registry that lets the widget refresh orchestrator ask the mounted
 * `WidgetChartHost` to rasterize the stats sparkline to PNGs (light + dark).
 *
 * The host (mounted once near the app root) registers a capturer on mount and
 * clears it on unmount. When the app is backgrounded the host may be gone; in
 * that case `captureWidgetChart` resolves `null` and the widgets simply keep
 * their previous chart image while text/numbers still refresh. This keeps the
 * chart-rendering concern (which needs a live React tree) decoupled from the
 * headless refresh path.
 */

/** @type {((series: number[]) => Promise<{ light: string|null, dark: string|null }>)|null} */
let capturer = null;

/**
 * Register the host's capture implementation. Called by `WidgetChartHost`.
 * @param {(series: number[]) => Promise<{ light: string|null, dark: string|null }>} fn
 * @returns {void}
 */
export const registerChartCapturer = (fn) => {
    capturer = typeof fn === 'function' ? fn : null;
};

/** Clear the registered capturer (host unmount). */
export const unregisterChartCapturer = () => {
    capturer = null;
};

/**
 * Rasterize the stats sparkline for the given series into base64 PNGs.
 * @param {number[]} series Numeric data points.
 * @returns {Promise<{ light: string|null, dark: string|null }>} base64 PNGs, or nulls.
 */
export const captureWidgetChart = async (series) => {
    if (!capturer || !Array.isArray(series) || series.length === 0) {
        return { light: null, dark: null };
    }

    try {
        const result = await capturer(series);

        return {
            light: result?.light || null,
            dark: result?.dark || null,
        };
    } catch (_error) {
        return { light: null, dark: null };
    }
};
